import React, { useEffect } from 'react'

import { styled } from 'styled-components'

import { useLocation } from '@cedarjs/router'

import SkipToContent, { MAIN_CONTENT_ID } from 'src/components/SkipToContent/SkipToContent'
import { useRouteAnnouncement } from 'src/hooks/use-route-announcement'
import { LayoutMode } from 'src/utils/layout'

import { useAuth } from '../../auth'
import { Sidebar } from '../../components/Sidebar/Sidebar'
import TopBar from '../../components/Topbar/Topbar'
import useDisclosure from '../../hooks/use-disclosure'
import SidebarLayout from '../SidebarLayout/SidebarLayout'

type Props = {
  children: React.ReactNode
  allowSidebarFallback?: boolean
  noChildrenPadding?: boolean
}

export const TopbarPageLayoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  max-height: 100vh;
  overflow-y: auto;
`

export const PageContent = styled.div<{ $padding?: boolean }>`
  height: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${(props) => props.$padding && 'padding: 24px 32px;'}
`

export const FooterContainer = styled.div`
  margin-top: auto;
`

const TopbarLayoutStandalone = ({ children }: Pick<Props, 'children'>) => {
  const modalState = useDisclosure()
  useRouteAnnouncement()
  const { pathname } = useLocation()
  useEffect(() => {
    modalState.close()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  return (
    <TopbarPageLayoutContainer>
      <SkipToContent />
      <TopBar onClickHamburger={modalState.toggle} hamburgerOpen={modalState.isOpen} />
      {!modalState.isOpen && (
        <div
          id={MAIN_CONTENT_ID}
          tabIndex={-1}
          className="flex h-full w-full flex-col overflow-y-auto outline-none"
        >
          {children}
        </div>
      )}
      {modalState.isOpen && <Sidebar layoutMode={LayoutMode.Topbar} />}
    </TopbarPageLayoutContainer>
  )
}

const TopbarLayout = (props: Props) => {
  const { allowSidebarFallback, children } = props

  const { isAuthenticated } = useAuth()

  const inSidebar = isAuthenticated && allowSidebarFallback

  if (inSidebar) {
    return <SidebarLayout>{children}</SidebarLayout>
  }

  return <TopbarLayoutStandalone>{children}</TopbarLayoutStandalone>
}

export default TopbarLayout
